// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.Dropdown.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

/**
 * Dropdown component
 */

import React, { Component } from 'react';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import ScrollBox from './Izenda.ScrollBox';
import FloatAffixed from './Izenda.FloatAffixed';
import UIContants from 'logic/data/Izenda.UIConstants';
import BrowserUtils from 'share/utils/Izenda.BrowserUtils';
import {
  get,
  map,
  find,
  keys,
  getOr,
  groupBy,
  isNil,
  isEqual,
  isEmpty,
  isString,
  isFunction,
  uniqueId
} from 'lodash/fp';

const HIDDEN_SELECTED_VALUE = '***HIDDEN VALUE***';

@observer
export default class DropDown extends Component {
  static propTypes = {
    id: PropTypes.string,
    options: PropTypes.oneOfType([PropTypes.object.isRequired, PropTypes.array.isRequired]),
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    valueField: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
    labelField: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
    groupBy: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
    maskedHidden: PropTypes.bool,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    groupFormatText: PropTypes.func,
    blankOption: PropTypes.bool,
    getOptionProperties: PropTypes.func,
    buildGroupLabel: PropTypes.func,
    optionTooltip: PropTypes.string,
    additionalKey: PropTypes.string,
    autoWidth: PropTypes.bool,
    renderInContainer: PropTypes.bool,
    additionalValueField: PropTypes.string,
    isOnPopover: PropTypes.bool,
    filterOptions: PropTypes.any
  };

  static defaultProps = {
    value: null,
    maskedHidden: false,
    valueField: 'value',
    labelField: 'label',
    iconField: 'icon',
    onChange: null,
    onFocus: null,
    disabled: false,
    blankOption: false,
    blankOptionText: '',
    optionTooltip: 'label',
    additionalValueField: null
  };

  state = {
    isOpen: false,
    selected: this.props.value,
    hiddenSelected: this.props.maskedHidden
  };

  componentWillReceiveProps(nextProps: any) {
    this.setState({
      selected: nextProps.value,
      hiddenSelected: nextProps.maskedHidden
    });
  }

  hideDropdown = () => {
    this.setState({ isOpen: false });
  };

  handleMouseDown = (event: any) => {
    const { isOnPopover, disabled } = this.props;
    if (isOnPopover && BrowserUtils.isIEBrowser()) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (disabled) return;

    this.props.onFocus && this.props.onFocus();

    this.setState({
      isOpen: !this.state.isOpen
    });
  };

  setValue = (value: string, label: string, selectedAdditionalValue: string) => {
    // Don't trigger change if don't change current value
    if (value === this.state.selected) {
      this.setState({ isOpen: false });
      return;
    }

    const oldValue = this.state.selected;

    this.setState({
      selected: value,
      isOpen: false,
      hiddenSelected: false,
      selectedAdditionalValue: selectedAdditionalValue
    });

    if (this.props.onChange) {
      const selectedItem = this.findSelectedOption(value, this.filteredOptions, selectedAdditionalValue);
      const change = { value: value, label: label, additionalValue: selectedAdditionalValue };

      Promise.resolve(this.props.onChange(change, selectedItem)).catch(() => {
        // revert to old value if the promise is reject
        this.setState({ selected: oldValue });
      });
    }
  };

  getValue = (option: any) => {
    const { valueField } = this.props;

    if (!valueField) {
      return option;
    } else if (isString(valueField)) {
      return get(valueField, option);
    } else if (isFunction(valueField)) {
      return valueField(option);
    }
  };

  getLabel = (option: any) => {
    const { labelField } = this.props;

    if (!labelField) {
      return option;
    } else if (isString(labelField)) {
      return get(labelField, option);
    } else if (isFunction(labelField)) {
      return labelField(option);
    }
  };

  getIcon = (option: any) => {
    const { iconField } = this.props;

    if (!iconField) {
      return option;
    } else if (isString(iconField)) {
      return get(iconField, option);
    } else if (isFunction(iconField)) {
      return iconField(option);
    }
  };

  renderOption = (option: any, index: number) => {
    const value = this.getValue(option);
    const label = this.getLabel(option);
    const icon = this.getIcon(option);
    const {
      props,
      props: { additionalKey }
    } = this;
    const key = additionalKey ? value + additionalKey : value;
    const tooltip = getOr(label, 'props.optionTooltip', option);
    const additionOptionProps = props.getOptionProperties && props.getOptionProperties(option);
    const additionalValue = props.additionalValueField ? getOr('', props.additionalValueField, option) : '';

    const optionClass = classnames({
      'izenda-Dropdown-option': true,
      'is-selected': value === this.state.selected
    });

    return (
      <div
        key={`${index}_${key}`}
        title={tooltip}
        className={optionClass}
        onClick={this.setValue.bind(this, value, label, additionalValue)}
        {...additionOptionProps}
      >
        {icon && <span className={icon} />}
        {label}
      </div>
    );
  };

  renderListOption = (options: any) => {
    return !isEmpty(options) ? options.map((item, index) => this.renderOption(item, index)) : [];
  };

  renderGroupOption = (options: any) => {
    const props = this.props;
    const optionsGrouped =
      groupBy(function(option) {
        return isFunction(props.groupBy) ? props.groupBy(option) : option[props.groupBy];
      }, options) || {};

    return map(name => {
      const groupValue = optionsGrouped[name];
      const title = props.groupFormatText ? props.groupFormatText(groupValue[0]) : '';
      const label = props.buildGroupLabel ? props.buildGroupLabel(groupValue[0]) : name;

      return label !== 'undefined' ? (
        <div className='izenda-Dropdown-group' key={name} label={label} title={title}>
          <div className='izenda-Dropdown-title u_textTruncate' title={label}>
            {label}
          </div>

          {this.renderListOption(groupValue)}
        </div>
      ) : (
        this.renderListOption(groupValue)
      );
    }, keys(optionsGrouped));
  };

  buildMenu = (filteredOptions: any) => {
    const { groupBy, blankOption, disabledBlankOption, blankOptionText } = this.props;
    const options = groupBy ? this.renderGroupOption(filteredOptions) : this.renderListOption(filteredOptions);

    !isEmpty(options) &&
      blankOption &&
      options.unshift(
        <div
          key={uniqueId('blankOption_')}
          className='izenda-Dropdown-option'
          disabled={disabledBlankOption}
          onClick={this.setValue.bind(this, '', '')}
        >
          {blankOptionText}
        </div>
      );

    this.state.hiddenSelected &&
      options.push(
        <div key={uniqueId('hide_')} className='hide' onClick={this.setValue.bind(this, HIDDEN_SELECTED_VALUE, '')}>
          {UIContants.MASKED_TEXT}
        </div>
      );

    return options;
  };

  componentDidUpdate() {
    const selectedValue = !isNil(this.state.selected) ? this.state.selected : '';
    const dropdown = this.dropdownRef;

    // sync value of control and component state
    if (dropdown && !selectedValue && dropdown.value !== selectedValue) {
      dropdown.value = selectedValue;
    }
  }

  findSelectedOption = (value: string, options: any, selectedAdditionalValue: string) => {
    const { additionalValueField } = this.props;

    return find(option => {
      return (
        isEqual(this.getValue(option), value) &&
        (!additionalValueField ||
          !selectedAdditionalValue ||
          isEqual(getOr('', additionalValueField, option), selectedAdditionalValue))
      );
    }, options);
  };

  filterOptions = () => {
    const { options, filterOptions } = this.props;
    if (isFunction(filterOptions)) {
      return filterOptions(options);
    }

    return options;
  };

  setDomNode = dom => {
    this.dropdownRef = dom;
  };

  render() {
    const { selected, hiddenSelected, selectedAdditionalValue, isOpen, optionTooltip } = this.state;
    const {
      defaultValue,
      disabled,
      blankOptionText,
      blankOption,
      className,
      isOnPopover,
      autoWidth,
      renderInContainer
    } = this.props;
    const options = (this.filteredOptions = this.filterOptions());
    const selectedValue = hiddenSelected ? HIDDEN_SELECTED_VALUE : !isNil(selected) ? selected : defaultValue;

    const selectedOption = this.findSelectedOption(selectedValue, options, selectedAdditionalValue);
    const selectedIcon = selectedOption && this.getIcon(selectedOption);
    let selectedText = selectedOption && this.getLabel(selectedOption);

    if (isNil(selectedOption)) {
      if (hiddenSelected) {
        selectedText = UIContants.MASKED_TEXT;
      } else if (blankOption) {
        selectedText = blankOptionText;
      }
    }

    const tooltip = getOr(selectedText, optionTooltip, selectedOption);
    const dropdownClass = classnames(className, {
      'izenda-Dropdown': true,
      'izenda-Dropdown_open': isOpen,
      'izenda-Dropdown_disabled': disabled
    });

    // Fix issue IE not fire event onClick on popover
    const onClickAction =
      isOnPopover && BrowserUtils.isIEBrowser()
        ? { onMouseDown: this.handleMouseDown }
        : { onClick: this.handleMouseDown };

    return (
      <div className={dropdownClass}>
        <div ref={this.setDomNode} className='izenda-Dropdown-control' title={tooltip} {...onClickAction}>
          <div className='izenda-Dropdown-placeholder'>
            {selectedIcon && <span className={selectedIcon} />}
            {selectedText}
          </div>
          <span className='izenda-Dropdown-arrow' />
        </div>

        {this.state.isOpen && (
          <FloatAffixed
            anchor={this.dropdownRef}
            autoWidth={autoWidth}
            renderInContainer={renderInContainer}
            onClickOutside={this.hideDropdown}
            onParentScroll={this.hideDropdown}
          >
            <div className='izenda-Dropdown-menu'>
              <ScrollBox autoHeight autoHeightMax={UIContants.dropdownMaxHeight}>
                {this.buildMenu(options)}
              </ScrollBox>
            </div>
          </FloatAffixed>
        )}
      </div>
    );
  }
}


// WEBPACK FOOTER //
// ./ui/share/components/controls/Izenda.DropDown.jsx